import { z } from 'zod';
/**
 * Signed media URLs are capabilities, not addresses (§4.8).
 *
 * The backend was explicit: no asset is publicly readable, a read URL is
 * short-lived and signed, and it "expires, so it is re-fetched by id rather
 * than cached or persisted."
 *
 * So this cache has a TTL SHORTER than the signature's, and nothing here ever
 * touches storage. Persisting a signed URL into localStorage or a service
 * worker cache would produce a card that renders for ten minutes and then
 * breaks with nothing in the code looking wrong.
 *
 * ── That rule split in two on 2026-08-10 ──────────────────────────────────
 * **Editorial** images — covers, hero banners, badge artwork, thumbnails — stop
 * being signed once S3 is enabled: permanent bucket URLs, no expiry, cached by
 * the browser for free. For those the TTL below is doing nothing, and the
 * `invalidate` path is dead code. Both are kept, because "expires sooner than
 * it needs to" costs a refetch and the alternative costs a broken image.
 *
 * **Audio, printables, course-module resources and member attachments stay
 * signed and still expire**, because a permanent URL to those would either end
 * the paywall or publish a parent's photograph. So the premise above is still
 * the one to reason from — it is simply no longer true of everything, and the
 * half it stopped being true of is the half nobody has to be careful about.
 *
 * ── Blocked today ─────────────────────────────────────────────────────────
 * `GET /api/media/{id}` requires CAPABILITY.ContentAuthor, which no member
 * holds, and `ContentBase` carries `coverAssetId` with no URL beside it. A
 * member cannot render a single cover image. Requested in
 * ../laboussle-backend/docs/requests-from-webapp.md §2; until it lands, every
 * resolution here fails and `<AssetImage>` shows its placeholder.
 */
export const MediaRef = z.object({
    id: z.string().uuid(),
    /** Null until the asset is READY. Nullable, never absent — the two differ. */
    url: z.string().nullable(),
    mimeType: z.string(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
});
/**
 * Deliberately short. The signature's own lifetime is the backend's business
 * and may change; ours only has to be safely inside it.
 */
const TTL_MS = 5 * 60 * 1000;
export class MediaResolver {
    fetchAsset;
    #cache = new Map();
    #inflight = new Map();
    constructor(fetchAsset) {
        this.fetchAsset = fetchAsset;
    }
    /** A cached URL, or null when there is nothing readable yet. */
    peek(id) {
        const entry = this.#cache.get(id);
        if (!entry)
            return undefined;
        if (Date.now() >= entry.expiresAt) {
            this.#cache.delete(id);
            return undefined;
        }
        return entry.url;
    }
    async resolve(id) {
        const cached = this.peek(id);
        if (cached !== undefined)
            return cached;
        const existing = this.#inflight.get(id);
        if (existing)
            return existing;
        // A grid of forty cards asks for forty different assets but must not ask
        // for the same one forty times.
        const promise = this.fetchAsset(id)
            .then((asset) => {
            this.#cache.set(id, { url: asset.url, expiresAt: Date.now() + TTL_MS });
            return asset.url;
        })
            .finally(() => this.#inflight.delete(id));
        this.#inflight.set(id, promise);
        return promise;
    }
    /**
     * Drop a cached URL so the next read re-signs it.
     *
     * Called by `<AssetImage>` when an `<img>` fails to load: an expired URL
     * fails silently — `naturalWidth` stays 0 and nothing throws — so the error
     * event is the only signal that the capability has run out.
     */
    invalidate(id) {
        this.#cache.delete(id);
    }
    clear() {
        this.#cache.clear();
        this.#inflight.clear();
    }
}
//# sourceMappingURL=media.js.map